import { useMemo, useRef, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Marked } from 'marked'
import DOMPurify from 'dompurify'
import { FileCitation } from '../../types/chat'
import { FileCitationBadge } from './FileCitationBadge'
import { Streamdown } from 'streamdown'

// Local Marked instance to avoid mutating global singleton
const citationMarked = new Marked({
  breaks: true,
  gfm: true,
  async: false,
})

interface InlineCitationContentProps {
  content: string
  citations: FileCitation[]
  className?: string
}

/**
 * Renders content with inline clickable citation badges using React portals.
 *
 * Approach: inject <span data-cite-idx> markers into raw markdown, parse the
 * entire content with marked.parse() (full block-level), then use createPortal
 * to mount FileCitationBadge React components into the marker spans.
 */
export function InlineCitationContent({ content, citations, className }: InlineCitationContentProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [portalTargets, setPortalTargets] = useState<Map<number, HTMLElement>>(new Map())

  const { hasValidCitations, fullHtml, citationMap } = useMemo(() => {
    if (!citations || citations.length === 0 || !content) {
      return { hasValidCitations: false, fullHtml: '', citationMap: new Map<number, FileCitation & { displayIndex: number }>() }
    }

    // Filter and sort citations with valid indices
    const validCitations = citations
      .map((c, originalIndex) => ({ ...c, originalIndex }))
      .filter(c => typeof c.index === 'number' && c.index >= 0 && c.index <= content.length)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))

    if (validCitations.length === 0) {
      return { hasValidCitations: false, fullHtml: '', citationMap: new Map<number, FileCitation & { displayIndex: number }>() }
    }

    // Build content with HTML marker spans injected at citation positions
    // Work backwards so insertion doesn't shift later indices
    let markedContent = content
    const cMap = new Map<number, FileCitation & { displayIndex: number }>()

    for (let i = validCitations.length - 1; i >= 0; i--) {
      const citation = validCitations[i]
      const idx = citation.index ?? 0
      const marker = `<span data-cite-idx="${i}"></span>`
      markedContent = markedContent.slice(0, idx) + marker + markedContent.slice(idx)
      cMap.set(i, { ...citation, displayIndex: citation.originalIndex + 1 })
    }

    // Parse full markdown (block-level) - HTML markers survive parsing
    const raw = citationMarked.parse(markedContent) as string
    const html = DOMPurify.sanitize(raw, { ADD_ATTR: ['data-cite-idx'] })

    return { hasValidCitations: true, fullHtml: html, citationMap: cMap }
  }, [content, citations])

  // After render, find marker spans in the DOM and set up portal targets
  useLayoutEffect(() => {
    if (!hasValidCitations || !containerRef.current) {
      setPortalTargets(new Map())
      return
    }

    const spans = containerRef.current.querySelectorAll<HTMLElement>('[data-cite-idx]')
    const targets = new Map<number, HTMLElement>()
    spans.forEach(span => {
      const idx = parseInt(span.getAttribute('data-cite-idx') || '', 10)
      if (!isNaN(idx)) {
        targets.set(idx, span)
      }
    })
    setPortalTargets(targets)

    return () => { setPortalTargets(new Map()) }
  }, [hasValidCitations, fullHtml])

  // Fallback: no valid citation indices, use Streamdown
  if (!hasValidCitations) {
    return (
      <div className={className}>
        <Streamdown>{content}</Streamdown>
        {citations && citations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <div className="text-xs text-muted-foreground mb-2">Sources:</div>
            <div className="flex flex-wrap gap-2">
              {citations.map((citation, i) => (
                <FileCitationBadge
                  key={i}
                  index={i + 1}
                  filename={citation.filename}
                  quote={citation.quote}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Full block-level markdown with citation marker spans */}
      <div
        ref={containerRef}
        dangerouslySetInnerHTML={{ __html: fullHtml }}
      />

      {/* Portal citation badges into marker spans */}
      {Array.from(portalTargets.entries()).map(([idx, el]) => {
        const citation = citationMap.get(idx)
        if (!citation) return null
        return createPortal(
          <FileCitationBadge
            key={`cite-${idx}`}
            index={citation.displayIndex}
            filename={citation.filename}
            quote={citation.quote}
          />,
          el
        )
      })}

      {/* Sources section */}
      <div className="mt-3 pt-3 border-t border-border/50">
        <div className="text-xs text-muted-foreground mb-2">Sources:</div>
        <div className="flex flex-wrap gap-2">
          {citations.map((citation, i) => (
            <FileCitationBadge
              key={i}
              index={i + 1}
              filename={citation.filename}
              quote={citation.quote}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
